import type { NodeViewProps } from "@tiptap/react";
import { useCallback } from "react";
import { CONTEXT_ATTR, isDidReference } from "./constants";
import { DynamicChipNodeView } from "./dynamic-chip-node";
import { useDynamicNodeContext } from "./dynamic-node-context";
import { consumeEditorOpen } from "./slash-command/data-key-registry";

/**
 * Context node: an inline chip resolving a context variable (device/org
 * characteristics + document metadata such as `organization-has-mdr-device`,
 * `documentIdentifier`) against the always-live backend context map. Looks and
 * behaves exactly like the data-key node — same shared chip — but its picker
 * lists the available context variables instead of the user's data keys.
 */
export const ContextNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}) => {
  const contextKey = (node.attrs[CONTEXT_ATTR] as string) ?? "";
  const { resolveContext, contextKeys, frozen, loading } =
    useDynamicNodeContext();
  const availableItems = contextKeys.filter(
    (item) => !isDidReference(item.key)
  );
  const selectNode = useCallback(() => {
    const position = getPos();
    if (typeof position === "number") {
      editor.chain().focus().setNodeSelection(position).run();
    }
  }, [editor, getPos]);

  return (
    <DynamicChipNodeView
      node={node}
      updateAttributes={updateAttributes}
      deleteNode={deleteNode}
      onSelect={selectNode}
      config={{
        attr: CONTEXT_ATTR,
        resolution: contextKey ? resolveContext(contextKey) : null,
        getItems: () => availableItems,
        consumePendingOpen: () => consumeEditorOpen("context"),
        loading,
        frozen,
        interactive: editor.isEditable,
        headerLabel: "Context",
        emptyLabel: "Select context",
        searchPlaceholder: "Search context…",
        noMatchLabel: "No matching context variables",
        renderResolvedAsPlainText: contextKey === "documentIdentifier",
      }}
    />
  );
};

export default ContextNodeView;
